/**
 * Route elements — maps every `routeManifest` id to what it renders.
 * `renderRoutes()` walks the manifest so the structure lives in one place.
 * `loaders` exposes each lazy chunk's import so the admin page navigator can
 * prove a page bundle actually loads (not just that the route is registered).
 */
import React, { lazy } from "react";
import { Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { routeManifest } from "./routeManifest";

// The quiz is the paid landing page: it ships in the main bundle so a
// Search click renders on the first round trip. Everything else is a
// route chunk (see memory/click_to_visit_audit.md — one 2 MB bundle was
// costing ~44% of paid mobile clicks before first paint).
import Quiz from "./pages/Quiz";

export const loaders = {
  home: () => import(/* webpackPrefetch: true */ "./pages/Landing"),
  login: () => import("./pages/Login"),
  register: () => import("./pages/Register"),
  "forgot-password": () => import("./pages/ForgotPassword"),
  "reset-password": () => import("./pages/ResetPassword"),
  "verify-email": () => import("./pages/VerifyEmail"),
  about: () => import("./pages/About"),
  contact: () => import("./pages/Contact"),
  "member-contact": () => import("./pages/MemberContact"),
  privacy: () => import("./pages/Privacy"),
  terms: () => import("./pages/Terms"),
  strategy: () => import("./pages/Strategy"),
  "habit-loop-strategy": () => import("./pages/HabitLoopStrategy"),
  unsubscribed: () => import("./pages/Unsubscribed"),
  "money-pillars": () => import("./pages/MoneyPillars"),
  "income-strategies": () => import("./pages/IncomeStrategies"),
  "habit-stacking": () => import("./pages/HabitStacking"),
  "wealth-formula": () => import("./pages/WealthFormula"),
  "spending-plan-vs-budget": () => import("./pages/SpendingPlanVsBudget"),
  compare: () => import("./pages/Compare"),
  "ynab-alternative": () => import("./pages/YnabAlternative"),
  "balance-calculator": () => import("./pages/BalanceCalculator"),
  press: () => import("./pages/Press"),
  why: () => import("./pages/Why"),
  couples: () => import("./pages/Couples"),
  "member-couples": () => import("./pages/CouplesApp"),
  "contest-rules": () => import("./pages/ContestRules"),
  dashboard: () => import("./pages/Dashboard"),
  calendar: () => import("./pages/Calendar"),
  bills: () => import("./pages/Bills"),
  habits: () => import("./pages/Habits"),
  "net-worth": () => import("./pages/NetWorth"),
  "whats-new": () => import("./pages/WhatsNew"),
  "new-device": () => import("./pages/NewDeviceLanding"),
  "admin-users": () => import("./pages/AdminUsers"),
  "admin-metrics": () => import("./pages/AdminMetrics"),
  "admin-cmo": () => import("./pages/AdminCMO"),
  "admin-cmo-videos": () => import("./pages/AdminCMOVideos"),
  "admin-cmo-creative": () => import("./pages/AdminCMOCreative"),
  "admin-cmo-library": () => import("./pages/AdminCMOLibrary"),
  "admin-cmo-operations": () => import("./pages/AdminCMOOperations"),
  pricing: () => import("./pages/Pricing"),
  settings: () => import("./pages/Settings"),
  "admin-inbox": () => import("./pages/AdminInbox"),
  "admin-chat": () => import("./pages/AdminChatInbox"),
  "admin-broadcast": () => import("./pages/AdminBroadcast"),
  "wealth-plan": () => import("./pages/WealthPlan"),
  links: () => import("./pages/Links"),
  "preview-ugc": () => import("./pages/UgcPreview"),
  "preview-half-loop": () => import("./pages/HalfLoopPreview"),
  scan: () => import("./pages/Scan"),
  app: () => import("./components/AppShell"),
};

const L = Object.fromEntries(Object.entries(loaders).map(([id, load]) => [id, lazy(load)]));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]" data-testid="auth-loading">
        <p className="font-serif text-2xl text-[#787671]">Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const { search } = useLocation();
  if (loading) return null;
  if (user) {
    // Honour ?next=/app/... (quiz result → register → persona Habit Loop);
    // otherwise this redirect would win the race against Register's nav().
    const next = new URLSearchParams(search).get("next") || "";
    return <Navigate to={next.startsWith("/app/") ? next : "/app"} replace />;
  }
  return children;
}

// Rescues malformed quiz links like /quiz/ref=youtube&utm_source=... (missing
// "?"). Rebuilds the path segment into a real query string so attribution
// survives — several such links are already posted publicly on videos.
function QuizPathFixRedirect() {
  const location = useLocation();
  const segment = location.pathname.replace(/^\/quiz\/?/, "");
  const merged = [segment, location.search.replace(/^\?/, "")]
    .filter(Boolean)
    .join("&");
  return <Navigate to={`/quiz${merged ? `?${merged}` : ""}`} replace />;
}

const Landing = L.home;
const AppShell = L.app;
const Login = L.login;
const Register = L.register;
const ForgotPassword = L["forgot-password"];
const StrategyPage = L.strategy;

const elements = {
  ...Object.fromEntries(Object.keys(loaders).map((id) => [id, React.createElement(L[id])])),
  home: <Landing />,
  quiz: <Quiz />,
  "member-quiz": <Quiz />,
  "member-strategy": <StrategyPage />,
  login: <PublicOnly><Login /></PublicOnly>,
  register: <PublicOnly><Register /></PublicOnly>,
  "forgot-password": <PublicOnly><ForgotPassword /></PublicOnly>,
  "quiz-path-fix": <QuizPathFixRedirect />,
  app: <Protected><AppShell /></Protected>,
  "admin-operations-redirect": <Navigate to="/app/admin/cmo/operations" replace />,
  "admin-cmo-metrics-redirect": <Navigate to="/app/admin/metrics" replace />,
  "catch-all": <Navigate to="/" replace />,
};

/** Build the <Route> tree from the manifest. A manifest id with no element is a bug. */
export function renderRoutes(nodes = routeManifest) {
  return nodes.map((n) => {
    const element = elements[n.id];
    if (!element) {
      if (process.env.NODE_ENV !== "production") throw new Error(`routes.jsx: no element for route "${n.id}"`);
      console.error(`routes.jsx: no element for route "${n.id}"`);
      return null;
    }
    return n.index ? (
      <Route key={n.id} index element={element} />
    ) : (
      <Route key={n.id} path={n.path} element={element}>
        {n.children ? renderRoutes(n.children) : null}
      </Route>
    );
  });
}
